import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MessageCircle, Clock, Banknote, FileText, GraduationCap, ClipboardList } from "lucide-react";
import { api, useSeo, waLink } from "../lib/api";
import { PageHero, Section, Loading, Empty, Text } from "../components/Common";
import { CourseCard, EventRow } from "../components/Cards";
import ApplyForm from "../components/ApplyForm";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

export function Courses() {
  const [items, setItems] = useState(null);
  useSeo("Eğitimlerimiz", "Kuşadası silahlı ve silahsız özel güvenlik temel eğitimi, yenileme eğitimi ve silah farkı eğitimi programları.");
  useEffect(() => { api.get("/courses").then((r) => setItems(r.data)); }, []);
  return (
    <>
      <PageHero eyebrow="Programlar" title="Eğitimlerimiz" text="Her programın detay sayfasında amaç, başvuru şartları, ders programı, sınav süreci ve başvuru formu yer alır." />
      <Section>{!items ? <Loading /> : <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{items.map((c, i) => <CourseCard key={c.id} c={c} i={i} />)}</div>}</Section>
    </>
  );
}

const Block = ({ icon: I, title, children, testid }) => (
  <div className="card-ada p-6" data-testid={testid}>
    <h2 className="flex items-center gap-2 font-black text-lg mb-3"><I size={18} className="text-[#dc2626]" />{title}</h2>
    {children}
  </div>
);

export function CourseDetail() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { setC(null); api.get(`/courses/${slug}`).then((r) => setC(r.data)).catch(() => setErr(true)); }, [slug]);
  useSeo(c?.title, c ? `${c.title} – Kuşadası / Aydın. ${c.summary}` : "");
  if (err) return <Section><Empty text="Eğitim bulunamadı." /></Section>;
  if (!c) return <Loading />;
  const wa = waLink(`Merhaba, ${c.title} hakkında bilgi almak istiyorum.`);
  return (
    <>
      <PageHero eyebrow="Eğitim Detayı" title={c.title} text={c.summary}>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="#basvuru" className="btn-red" data-testid="course-apply-anchor">Başvuru Yap</a>
          <a href={wa} target="_blank" rel="noreferrer" className="btn-outline text-white" data-testid="course-whatsapp-btn"><MessageCircle size={16} />WhatsApp ile Bilgi Al</a>
        </div>
      </PageHero>
      <Section>
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2 grid gap-4">
            <Block icon={GraduationCap} title="Eğitimin amacı ve kimler katılabilir" testid="course-purpose"><Text value={c.purpose} fallback="[Eklenecek]" /><Text value={c.audience} fallback="" /></Block>
            <Block icon={ClipboardList} title="Başvuru şartları" testid="course-requirements">
              <ul className="list-disc pl-5 space-y-1.5 text-sm text-zinc-700">{(c.requirements || []).map((r) => <li key={r}>{r}</li>)}</ul>
              {c.requirements_note && <p className="mt-3 text-xs text-zinc-500 border-l-2 border-zinc-300 pl-3">{c.requirements_note}</p>}
            </Block>
            <Block icon={FileText} title="Ders programı, teorik ve uygulamalı eğitim" testid="course-program">
              <Text value={c.program} fallback="Ders programı yönetici tarafından yayınlandığında burada yer alacaktır." />
              <Text value={c.theory_practice} fallback="" />
            </Block>
            <Block icon={ClipboardList} title="Sınav süreci" testid="course-exam"><Text value={c.exam_process} fallback="Sınav süreci bilgisi için kurumumuzla iletişime geçin." /></Block>
            <Block icon={FileText} title="Gerekli belgeler" testid="course-documents">
              {c.documents_required?.length ? <ul className="list-disc pl-5 space-y-1.5 text-sm">{c.documents_required.map((d) => <li key={d}>{d}</li>)}</ul> : <p className="text-sm text-zinc-500">Kayıt aşamasında gerekli belgeler size ayrıca bildirilir.</p>}
            </Block>
            {!!c.faqs?.length && (
              <Block icon={MessageCircle} title="Sık sorulan sorular" testid="course-faqs">
                <Accordion type="single" collapsible>{c.faqs.map((f, i) => <AccordionItem key={i} value={`f${i}`}><AccordionTrigger className="text-left">{f.q}</AccordionTrigger><AccordionContent>{f.a}</AccordionContent></AccordionItem>)}</Accordion>
              </Block>
            )}
          </div>
          <aside className="grid gap-4 content-start">
            <div className="bg-zinc-950 text-white p-6">
              <p className="flex items-center gap-2 text-sm text-zinc-400"><Clock size={16} />Eğitim süresi</p>
              <p className="mt-1 font-black text-xl" data-testid="course-duration">{c.duration || "Güncel süre için bizimle iletişime geçin"}</p>
              <p className="mt-5 flex items-center gap-2 text-sm text-zinc-400"><Banknote size={16} />Ücret</p>
              <p className="mt-1 font-black text-xl" data-testid="course-price">{c.price || "Bilgi alınız"}</p>
              <p className="text-xs text-zinc-400 mt-1">{c.price_note}</p>
            </div>
            <div className="card-ada p-5"><h3 className="font-bold mb-2">Hızlı iletişim</h3><a href={wa} target="_blank" rel="noreferrer" className="btn-red w-full !bg-[#25D366] hover:!bg-[#1da851]" data-testid="course-whatsapp-side"><MessageCircle size={16} />WhatsApp</a></div>
          </aside>
        </div>
      </Section>
      <Section eyebrow="Takvim" title="Kurs dönemleri" className="!pt-0">
        <div className="grid gap-3">{c.events?.length ? c.events.map((e) => <EventRow key={e.id} e={e} />) : <Empty text="Bu eğitim için yayınlanmış dönem bulunmuyor. Bilgi talebi bırakabilirsiniz." />}</div>
      </Section>
      <Section eyebrow="Başvuru" title={`${c.title} – Ön başvuru`} className="!pt-0" >
        <div id="basvuru" className="max-w-3xl card-ada p-6 sm:p-8"><ApplyForm courseId={c.id} /></div>
        <p className="mt-4 text-sm"><Link to="/egitimlerimiz" className="underline">← Tüm eğitimler</Link></p>
      </Section>
    </>
  );
}
